import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import varistLogo from "../assets/varist-navbar-logo.svg";
import homeIcon from "../assets/home.svg";
import "./AllScans.scss";
import { toggleFavorite } from "../lib/api";
import { trackSearchResults } from "../analytics";
import { useAuth } from "../context/AuthContext";
import { useSignInModal } from "../context/SignInModalContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  searchAnalysesOptions,
  availableObjectTypesOptions,
  analysisKeys,
} from "../lib/queries";
import AnalysisList from "./AnalysisList";

export default function AllScans() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { openSignInModal } = useSignInModal();
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState("");
  const [objectTypes, setObjectTypes] = useState<string[]>([]);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState<Set<string>>(
    new Set(),
  );

  const [committedQuery, setCommittedQuery] = useState("");
  const [committedTypes, setCommittedTypes] = useState<string[]>([]);

  useEffect(() => {
    const searchQuery = location.state?.searchQuery || "";
    const selectedTypes = location.state?.selectedObjectTypes || [];
    setQuery(searchQuery);
    setObjectTypes(selectedTypes);
    setCommittedQuery(searchQuery);
    setCommittedTypes(selectedTypes);
  }, [location.state?.searchQuery, location.state?.selectedObjectTypes]);

  const { data: availableTypes = [] } = useQuery(availableObjectTypesOptions());

  const {
    data: scans = [],
    isLoading: loading,
    isError,
    error,
  } = useQuery({
    ...searchAnalysesOptions(committedQuery, committedTypes),
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (committedQuery && scans.length > 0) {
      trackSearchResults(scans.length, committedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scans.length, committedQuery]);

  const favoriteMutation = useMutation({
    mutationFn: ({
      analysisId,
      isFavorite,
    }: {
      analysisId: string;
      isFavorite: boolean;
    }) => toggleFavorite(analysisId, isFavorite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analysisKeys.all });
    },
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCommittedQuery(query);
    setCommittedTypes(objectTypes);
  };

  const _handleClearFilters = () => {
    setQuery("");
    setObjectTypes([]);
    setShowTypeDropdown(false);
    setCommittedQuery("");
    setCommittedTypes([]);
  };

  const handleSelectType = (type: string) => {
    let updatedTypes: string[];
    if (objectTypes.includes(type)) {
      updatedTypes = objectTypes.filter((t) => t !== type);
    } else {
      updatedTypes = [...objectTypes, type];
    }
    setObjectTypes(updatedTypes);
    setCommittedQuery(query);
    setCommittedTypes(updatedTypes);
  };

  const handleRemoveType = (typeToRemove: string) => {
    const updatedTypes = objectTypes.filter((t) => t !== typeToRemove);
    setObjectTypes(updatedTypes);
    setCommittedQuery(query);
    setCommittedTypes(updatedTypes);
  };

  const handleScanClick = (analysisId: string) => {
    navigate(`/analysis/${analysisId}`);
  };

  const handleFavoriteToggle = async (
    e: React.MouseEvent,
    analysisId: string,
  ) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      openSignInModal();
      return;
    }

    if (favoriteLoading.has(analysisId)) return;

    const item = scans.find((s) => s.analysisId === analysisId);
    const newIsFavorite = !item?.isFavorite;
    setFavoriteLoading((prev) => new Set(prev).add(analysisId));

    try {
      await favoriteMutation.mutateAsync({ analysisId, isFavorite: newIsFavorite });
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    } finally {
      setFavoriteLoading((prev) => {
        const next = new Set(prev);
        next.delete(analysisId);
        return next;
      });
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 650;
  return (
    <div className="all-scans-container">
      {!isMobile && (
        <Link to="/" id="all-scans-home-lnk">
          <img className="all-scans-home-icon" src={homeIcon} alt="Home" />
        </Link>
      )}
      <div className="all-scans-header">
        <Link to="/" className="all-scans-logo-link">
          <img className="all-scans-logo" src={varistLogo} alt="Varist logo" />
        </Link>
        <h1>Recent Scans</h1>
        <p className="scan-count">
          {loading
            ? "Loading..."
            : (committedQuery || committedTypes.length > 0) &&
              `Results: ${scans.length} scans`}
        </p>
      </div>

      <div className="search-filter-container">
        <form onSubmit={handleSearch} className="searchbar">
          <img src="/search-grey.svg" alt="search" className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by filename or SHA256..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {objectTypes.length > 0 && (
            <div className="active-filters-container">
              {objectTypes.map((type) => (
                <div key={type} className="active-filter-badge">
                  {type}
                  <button
                    type="button"
                    className="badge-clear-btn"
                    onClick={() => handleRemoveType(type)}
                    title="Remove filter"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="filter-dropdown-wrapper" ref={filterDropdownRef}>
            <button
              type="button"
              className="search-filter-btn"
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              title="Filter by object type"
            >
              <img src="/filter.svg" alt="filter" />
            </button>
            {showTypeDropdown && (
              <div className="filter-dropdown-menu">
                {availableTypes.length > 0 ? (
                  availableTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`filter-option ${objectTypes.includes(type) ? "active" : ""}`}
                      onClick={() => handleSelectType(type)}
                    >
                      {type}
                    </button>
                  ))
                ) : (
                  <div className="filter-no-types">No types available</div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>

      {isError && (
        <div className="error-message">
          {error instanceof Error
            ? error.message
            : "Failed to load scans. Please try again later."}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <p>Loading scans...</p>
        </div>
      ) : scans.length === 0 ? (
        <div className="no-scans">
          <p>No scans found. Upload a file to get started!</p>
          <button onClick={handleBack} className="home-button">
            Go to Home
          </button>
        </div>
      ) : (
        <div className="scans-grid-wrapper">
          <AnalysisList
            items={scans}
            onFavoriteToggle={handleFavoriteToggle}
            onItemClick={handleScanClick}
          />
        </div>
      )}
    </div>
  );
}
